import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useField } from '@unform/core';
import { Autocomplete, TextField } from '@mui/material';
import i18n from '../../../services/i18n';

function InputSelectAutoComplete({
                                   name,
                                   label,
                                   options = [],
                                   placeholder = i18n.t('placeholder_select'),
                                   InputProps = {},
                                   defaultValue,
                                   freeSolo = false,
                                   ...props
                                 }) {
  const inputRef = useRef(null);
  const { fieldName, registerField, error } = useField(name);

  const [selected, setSelected] = useState(() => {
    if (freeSolo) {
      return null;
    }

    return options.find(option => option.key === defaultValue) || null;
  });
  const [inputValue, setInputValue] = useState(() => {
    if (!defaultValue) {
      return '';
    }

    if (freeSolo) {
      return defaultValue;
    }

    return options.find(option => option.key === defaultValue)?.value || '';
  });

  // Atualiza selected se defaultValue mudar (ex: após fetch)
  useEffect(() => {
    if (freeSolo) {
      setInputValue(defaultValue || '');
      if (inputRef.current) {
        inputRef.current.value = defaultValue || '';
      }
      return;
    }

    const found = options.find(option => option.key === defaultValue) || null;
    setSelected(found);
    setInputValue(found?.value || '');
    if (inputRef.current) {
      inputRef.current.value = found?.key || '';
    }
  }, [defaultValue, freeSolo, options]);

  // Unform integration
  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      getValue: () => inputRef.current?.value || '',
      setValue: (_, value) => {
        if (freeSolo) {
          setInputValue(value || '');
          if (inputRef.current) {
            inputRef.current.value = value || '';
          }
          return;
        }

        const found = options.find(option => option.key === value);
        if (found) {
          setSelected(found);
          setInputValue(found.value || '');
        }
      },
      clearValue: () => {
        setSelected(null);
        setInputValue('');
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      },
    });
  }, [fieldName, freeSolo, registerField, options]);

  // Atualiza valor do input hidden
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = freeSolo ? inputValue : (selected?.key || '');
    }
  }, [freeSolo, inputValue, selected]);

  return (
    <>
      <input type="hidden" ref={inputRef} name={name} />
      <Autocomplete
        style={{ width: '100%' }}
        options={options}
        getOptionLabel={(option) => option?.value || ''}
        freeSolo={freeSolo}
        value={selected}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        onChange={(_, newValue) => {
          if (freeSolo) {
            if (typeof newValue === 'string') {
              setSelected(null);
              setInputValue(newValue);
              return;
            }

            setSelected(newValue);
            setInputValue(newValue?.value || '');
            return;
          }

          setSelected(newValue);
        }}
        isOptionEqualToValue={(option, value) => option.key === value?.key}
        disablePortal
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={!!error}
            helperText={error}
            fullWidth
            InputProps={{
              ...params.InputProps,
              ...InputProps, // <-- Suporte a adornos, etc.
            }}
          />
        )}
        {...props}
      />
    </>
  );
}

InputSelectAutoComplete.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  freeSolo: PropTypes.bool,
  InputProps: PropTypes.object,
};

export default InputSelectAutoComplete;
